/**
 * 외부 공유 페이지
 * 결재 완료된 보고서를 로그인 없이 공유 토큰으로 조회
 */

import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Badge, Button, Card, Modal } from '@safetyhub/ui';
import api, { API_HOST, extractApiError, type ApiError } from '../../lib/api';
import { getSharedReport } from '../../lib/share';
import {
  formatDateToLocal,
  getEvidenceDisplayName,
} from '@safetyhub/shared';
import type { SharedReportData } from '../../utils/reportNormalizer';
import { ComposedDocumentBody } from '../../components/reports/composer/ComposedDocumentBody';
import { buildComposerApprovalDataFromShare } from '../../composer/composerApprovalDataPublic';

type PageState = 'loading' | 'ready' | 'error';

export default function SharedReportPage() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [pageState, setPageState] = useState<PageState>('loading');
  const [reportData, setReportData] = useState<SharedReportData | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfModal, setPdfModal] = useState<{ show: boolean; message: string; type: 'info' | 'error' }>({
    show: false,
    message: '',
    type: 'info',
  });

  // 보고서 정보 조회 (공개 API - 인증 헤더 제외)
  useEffect(() => {
    if (!token) return;

    const fetchReportInfo = async () => {
      try {
        setReportData(await getSharedReport(token));
        setPageState('ready');
      } catch (err) {
        // 서버가 코드로 거절한 경우(`REPORT_SHARE_*`)는 그 코드·문구를 그대로 제목·본문에 쓴다.
        // 종전엔 axios 거부를 통째로 「연결 오류」로 뭉개 어떤 서버 코드도 여기 닿지 못했다.
        const apiError = extractApiError(err);
        setError(
          apiError.code === 'NETWORK_ERROR' || apiError.code === 'UNKNOWN_ERROR'
            ? {
                code: 'NETWORK_ERROR',
                message: '서버에 연결할 수 없습니다. 잠시 후 다시 시도해주세요.',
              }
            : apiError,
        );
        setPageState('error');
      }
    };

    fetchReportInfo();
  }, [token]);

  // 모든 공유 문서는 Composer 양식 문서라 렌더 코어(`ComposedSharedView`) 하나다 — 양식 전용 뷰를 두지 않는다.

  // PDF 다운로드 (공개 API - 인증 헤더 제외)
  const handlePdfDownload = async () => {
    if (!token) return;

    setPdfLoading(true);
    try {
      // 공개 API이므로 인증 헤더 없이 호출 (skipAuth 플래그 사용)
      const response = await api.get(`/share/${token}/pdf`, {
        skipAuth: true,
      } as any);
      if (response.data.success && response.data.data.fileUrl) {
        // 새 탭에서 PDF 열기
        // fileUrl이 이미 절대 URL(http로 시작)이면 그대로 사용, 아니면 API_HOST 붙임
        const fileUrl = response.data.data.fileUrl;
        const fullUrl = fileUrl.startsWith('http') ? fileUrl : `${API_HOST}${fileUrl}`;
        window.open(fullUrl, '_blank');
      } else {
        // PDF 생성 중이거나 에러인 경우 모달 표시
        const errorCode = response.data.error?.code;
        const isGenerating = errorCode === 'PDF_GENERATING';
        setPdfModal({
          show: true,
          message: response.data.error?.message || 'PDF를 불러올 수 없습니다.',
          type: isGenerating ? 'info' : 'error',
        });
      }
    } catch {
      setPdfModal({
        show: true,
        message: 'PDF를 불러오는 중 오류가 발생했습니다.',
        type: 'error',
      });
    } finally {
      setPdfLoading(false);
    }
  };

  // PDF 모달 닫기
  const closePdfModal = () => {
    setPdfModal({ show: false, message: '', type: 'info' });
  };

  // 로딩 상태
  if (pageState === 'loading') {
    return (
      <div className="min-h-screen bg-ink-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-600 mx-auto"></div>
          <p className="mt-4 text-ink-600">보고서를 불러오는 중...</p>
        </div>
      </div>
    );
  }

  // 에러 상태
  if (pageState === 'error' && error) {
    return (
      <div className="min-h-screen bg-ink-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-card shadow-lg p-8 text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-100 flex items-center justify-center">
            <ErrorIcon className="h-8 w-8 text-red-600" />
          </div>
          <h1 className="text-xl font-semibold text-ink-900 mb-2">
            {getErrorTitle(error.code)}
          </h1>
          <p className="text-ink-600 mb-6">{error.message}</p>
          <div className="text-sm text-ink-500">
            <p>문제가 지속되면 담당자에게 문의해주세요.</p>
          </div>
        </div>
      </div>
    );
  }

  if (!reportData) return null;

  // Composer 양식 전용 뷰 (상세와 동일한 ComposedDocumentBody 재사용).
  return (
    <>
      <ComposedSharedView
        data={reportData}
        pdfLoading={pdfLoading}
        onPdfDownload={handlePdfDownload}
        onClaimCompany={() => navigate(`/claim-company?token=${token}`)}
      />

      {/* PDF 상태 모달 — 종전엔 구 일반 보고서 갈래의 return에만 있어 Composer 문서의 PDF 생성 중·
          오류 안내가 화면에 닿지 않았다(상태만 세팅). 라운드 3에서 갈래를 접으며 공통 자리로 올렸다. */}
      {/* base로 옮기며: 아이콘+제목이 본문 머리라 base 헤더를 쓰지 않는다(종전에도 ✕ 없음). 종전엔
          오버레이 클릭 닫기도 없었으므로 그대로 끄고, 닫는 길은 「확인」과 Esc(base가 새로 준다)다. */}
      <Modal
        open={pdfModal.show}
        onClose={closePdfModal}
        ariaLabel={pdfModal.type === 'info' ? 'PDF 생성 중' : 'PDF 오류'}
        size="sm"
        showCloseButton={false}
        closeOnOverlay={false}
        footer={<Button onClick={closePdfModal}>확인</Button>}
      >
            <div className="flex flex-col sm:flex-row sm:items-start gap-3 sm:gap-4">
              <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center mx-auto sm:mx-0 ${
                pdfModal.type === 'info' ? 'bg-blue-100' : 'bg-red-100'
              }`}>
                {pdfModal.type === 'info' ? (
                  <InfoIcon className={`w-5 h-5 text-blue-600`} />
                ) : (
                  <ErrorIcon className={`w-5 h-5 text-red-600`} />
                )}
              </div>
              <div className="flex-1 text-center sm:text-left">
                <h3 className="text-lg font-medium text-ink-900 mb-2">
                  {pdfModal.type === 'info' ? 'PDF 생성 중' : 'PDF 오류'}
                </h3>
                <p className="text-sm text-ink-600">{pdfModal.message}</p>
              </div>
            </div>
      </Modal>
    </>
  );
}

/**
 * COMPOSED(Composer 양식) 공유 전용 뷰 — 상세(ComposerReportDetail)와 동일한 ComposedDocumentBody를
 * 재사용해 파리티를 구조적으로 보장한다. 공개 경로라 live fetch 없이(reportId 미전달) 스냅샷 content로 렌더.
 */
function ComposedSharedView({
  data,
  pdfLoading,
  onPdfDownload,
  onClaimCompany,
}: {
  data: SharedReportData;
  pdfLoading: boolean;
  onPdfDownload: () => void;
  onClaimCompany: () => void;
}) {
  // 결재란 데이터 — 인쇄 미리보기와 동형으로 data가 바뀔 때만 조립(매 렌더 호출 방지, W-h).
  const approvalData = useMemo(() => buildComposerApprovalDataFromShare(data), [data]);
  return (
    <div className="min-h-screen bg-ink-50 py-4 sm:py-8 px-4">
      <div className="max-w-4xl xl:max-w-6xl mx-auto">
        {/* 툴바 */}
        <Card padding="none" radius="lg" elevation="md" className="mb-4 px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <Badge tone="emerald" shape="rounded">
                  결재 완료
                </Badge>
                <h1 className="text-sm font-medium text-ink-900 truncate">{data.title}</h1>
              </div>
              <p className="text-xs text-ink-500 mt-0.5 truncate">
                {data.site?.name ?? ''} | {getEvidenceDisplayName(data.authorSignature?.signerName ?? null, { name: data.author.name ?? '' })} | {formatDateToLocal(new Date(data.createdAt))}
              </p>
            </div>
            <div className="ml-3 flex-shrink-0 flex items-center gap-2">
              <Button size="sm" onClick={onClaimCompany}>
                SafetyHub 가입
              </Button>
              <Button variant="primary" size="sm" onClick={onPdfDownload} disabled={pdfLoading}>
                {pdfLoading ? (
                  <>
                    <LoadingSpinner className="w-3.5 h-3.5" />
                    PDF...
                  </>
                ) : (
                  <>
                    <DownloadIcon className="w-3.5 h-3.5" />
                    PDF
                  </>
                )}
              </Button>
            </div>
          </div>
        </Card>

        {/* 양식 본문 — 상세와 동일한 공용 렌더 코어(ComposedDocumentBody). */}
        {/* 여백이 화면폭별로 갈려(p-2 sm:p-4) padding prop을 끄고 className으로 준다 */}
        <Card padding="none" radius="lg" elevation="md" className="p-2 sm:p-4">
          <ComposedDocumentBody
            templateSnapshot={data.templateSnapshot ?? undefined}
            templateKey={data.templateKey}
            content={data.content}
            approvalData={approvalData}
            // 무인증 경로 — 서버가 상세와 동일 resolver로 부착한 live 바인딩(서명 등) 현재값 blob 직접 주입.
            liveData={data.liveData ?? undefined}
          />
        </Card>
      </div>
    </div>
  );
}

// 에러 타이틀 — 키는 서버 `report-share.controller`가 던지는 코드(shared `errors/codes.ts`)와 같아야 한다.
// 종전 `SHARE_TOKEN_*`·`REPORT_NOT_APPROVED`는 서버에 없는 이름이라 어떤 거절도 제목을 못 받았다(라운드 5 INFO).
function getErrorTitle(code: string): string {
  const titles: Record<string, string> = {
    REPORT_SHARE_NOT_FOUND: '링크를 찾을 수 없습니다',
    REPORT_SHARE_EXPIRED: '링크가 만료되었습니다',
    REPORT_SHARE_NOT_APPROVED: '결재 미완료',
    NETWORK_ERROR: '연결 오류',
  };
  return titles[code] || '오류가 발생했습니다';
}

// 아이콘 컴포넌트
function ErrorIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
      />
    </svg>
  );
}

function InfoIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
      />
    </svg>
  );
}

function DownloadIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
      />
    </svg>
  );
}

function LoadingSpinner({ className }: { className?: string }) {
  return (
    <svg className={`animate-spin ${className}`} fill="none" viewBox="0 0 24 24">
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      />
    </svg>
  );
}
